/**
 * 판매보고서 정보창 (Phase 6-3 보강 · v1.5.0 결재 도입).
 *
 * 영업건 상세 모달의 "판보작성" 버튼 / 내 결재함 목록 클릭으로 열림 — 두 곳 모두 같은
 * 데이터(영업건 실시간 조회)를 보여주고, 수정·저장도 동일하게 반영됨.
 *
 * 결재 흐름 (담당 단계 포함):
 *   [판보 저장] → 검토대기 (내 결재함 등재. 재저장 시 처음부터 다시)
 *   담당 승인 → 담당승인 → 부서장(js) 승인 → 부서장승인 → 대표이사(shlee) 승인 → 최종승인 (확정)
 *   반려 가능(사유). 최종승인 후에는 수정 잠금 — [승인 회수] 후 가능.
 *   결재판 4칸: 작성자(최초 저장자·작성일) / 담당(영업건 담당자) / 부서장 / 대표이사 — 각 칸에
 *   서명(마이페이지 등록) 또는 이름 텍스트 + 아래 작성일/승인일. 최초 저장 시엔 작성자만 서명.
 *
 * ⭐ 판보 '확정'(진행단계 → 판보)은 여기서 하지 않음 (2026-07-14). 결재를 거쳐야 하므로
 *    대표이사 최종승인 시 백엔드에서 자동으로 진행단계가 '판보'로 전환됨. 이 창은 저장(결재 상신)
 *    까지만 담당. 영업진행에서 판보가 이미 저장된 건은 [판보 작성] 버튼이 비활성화되고 결재
 *    상태만 표시된다 (수정은 내 결재함에서).
 *
 * 기타비용 (v1.5.0): [+ 기타비용] 로 숙박비·운송비 등 자유입력 — 원가·손익에 합산.
 */

import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { useDialogLayer } from '../lib/dialog-stack';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import * as salesApi from '../lib/sales-api';
import * as approvalApi from '../lib/approval-api';
import * as purchaseApi from '../lib/purchase-api';
import { listAttachments, previewAttachment, type AttachmentMeta } from '../lib/attachment-api';
import { quotePdfDownloadUrl } from '../lib/quote-api';
import { openServerPdfPreview } from '../lib/pdf-preview';
import type { LinkedQuote, ReportApprovalInfo, SalesDetail } from '../lib/types';
import { useAuth } from '../lib/auth-context';
import { syncAllData } from '../lib/invalidate';
import { stageBadgeClass } from '../lib/sales-stage';
import { MiscCostModal } from './MiscCostModal';
import { DraggableModal } from './DraggableModal';
import { useDirtyClose } from '../lib/useDirtyClose';
import { PurchaseAuditSection } from './PurchaseAuditSection';
import { LoadingLines } from './ui/Skeleton';

// 매입 연결 모달 lazy — [+ 매입 연결] 클릭 시 처음 로드.
// 새 매입 행을 만드는 게 아니라 매입관리의 기존 재고보유 매입을 영업에 연결.
const LinkPurchaseModal = lazy(() =>
  import('./LinkPurchaseModal').then((m) => ({ default: m.LinkPurchaseModal })),
);

// 첨부(발주서·분납서) 2건 이상일 때 선택 모달 — 승인 검토용 아이콘에서 사용.
const AttachmentDownloadModal = lazy(() =>
  import('./AttachmentDownloadModal').then((m) => ({ default: m.AttachmentDownloadModal })),
);

const APPROVAL_BADGE: Record<string, string> = {
  검토대기: 'bg-amber-100 text-amber-800 border-amber-200',
  반려: 'bg-rose-100 text-rose-700 border-rose-200',
  담당승인: 'bg-sky-100 text-sky-800 border-sky-200',
  부서장승인: 'bg-blue-100 text-blue-800 border-blue-200',
  최종승인: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

/** 현재 상태에서 [승인] 시 다음 상태 (버튼 확인문구용) */
const NEXT_STATUS: Record<string, string> = {
  검토대기: '담당승인',
  담당승인: '부서장승인',
  부서장승인: '최종승인',
};

function fmtKrw(n: number): string {
  return n.toLocaleString('ko-KR');
}
function fmtPct(n: number | null): string {
  if (n === null) return '-';
  return `${n.toFixed(1)}%`;
}
function fmtShortDate(ts: string | null): string | null {
  if (!ts) return null;
  const m = ts.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]!.slice(2)}.${m[2]}.${m[3]}` : null;
}

interface Props {
  detail: SalesDetail;
  onClose: () => void;
}

export function SalesReportModal({ detail, onClose }: Props) {
  const { user } = useAuth();
  const canAddPurchase = user?.role === 'sales' || user?.role === 'mgmt' || user?.role === 'admin';
  const canSaveReport = user?.role === 'sales' || user?.role === 'mgmt' || user?.role === 'admin';
  const qc = useQueryClient();
  const [creatingPurchase, setCreatingPurchase] = useState(false);
  const [managingMisc, setManagingMisc] = useState(false);
  const [reportNote, setReportNote] = useState<string>(detail.reportNote ?? '');
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfMsg, setPdfMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  // 매입 상세 팝업 (읽기 전용) — 매입 행 클릭 시 열림
  const [peekPurchaseId, setPeekPurchaseId] = useState<number | null>(null);
  // 묶음(합본) 판보 (v1.6.0) — 이 판보에 묶인 영업건 목록. [판보 저장] 시 함께 저장.
  const [memberIds, setMemberIds] = useState<string[]>(detail.reportMemberIds ?? []);
  const [pickingMembers, setPickingMembers] = useState(false);
  const isViewer = !user || user.role === 'viewer';

  // 승인 검토용 첨부 조회 (발주서·분납서·납품확인증 등) — viewer 제외.
  // kind 없이 한 번 조회해서 아이콘·문서 체크리스트가 공유 (요청 1회).
  const attachmentsQuery = useQuery({
    queryKey: ['attachments', 'sales', detail.salesId],
    queryFn: () => listAttachments('sales', detail.salesId),
    enabled: !isViewer,
    staleTime: 30_000,
  });
  const atts = attachmentsQuery.data ?? [];
  const poAtts = atts.filter((a) => a.kind === '발주서');
  const bunapAtts = atts.filter((a) => a.kind === '분할납품요구서');

  // ── 결재 상태 + 결재판 (v1.5.0) ──
  const approvalQuery = useQuery({
    queryKey: ['reportApproval', detail.salesId],
    queryFn: () => approvalApi.getReportApproval(detail.salesId),
    staleTime: 15_000,
    enabled: !!user && user.role !== 'viewer',
  });
  const approval = approvalQuery.data ?? null;
  const finalApproved = approval?.status === '최종승인';
  // 재저장은 최초 작성자(또는 admin)만. 아직 미등재(최초 저장)면 누구나 가능.
  const isAuthorOrNew =
    !approval?.status || approval.authoredBy === user?.userId || user?.role === 'admin';
  const canSave = canSaveReport && !finalApproved && isAuthorOrNew;
  // 삭제는 검토대기(최초작성) 상태에서 작성자 본인·admin 만 (승인 시작 전에만 되돌릴 수 있음).
  const canDelete =
    approval?.status === '검토대기' &&
    (approval.authoredBy === user?.userId || user?.role === 'admin');

  function afterApprovalChange() {
    // 결재 상태 변경은 영업 상세의 판보 결재 배지(reportApprovalStatus)에 반영되고,
    // 특히 대표이사 최종승인은 백엔드에서 sales.status 를 '판보'로 자동 전환하므로
    // 영업목록·상세·대시보드·매출분석까지 모두 갱신해야 화면이 즉시 정합. (syncAllData=전체 invalidate)
    syncAllData(qc);
  }

  const approveMut = useMutation({
    mutationFn: () => approvalApi.approveReport(detail.salesId),
    onSuccess: (r) => {
      setPdfMsg({ kind: 'ok', text: `승인 완료 — 상태: ${r.status}` });
      afterApprovalChange();
    },
    onError: (e: Error) => setPdfMsg({ kind: 'err', text: e.message }),
  });
  const rejectMut = useMutation({
    mutationFn: (reason: string) => approvalApi.rejectReport(detail.salesId, reason || undefined),
    onSuccess: () => {
      setPdfMsg({ kind: 'ok', text: '반려 처리했습니다 — 작성자가 수정 후 다시 저장하면 재검토됩니다.' });
      afterApprovalChange();
    },
    onError: (e: Error) => setPdfMsg({ kind: 'err', text: e.message }),
  });
  const recallMut = useMutation({
    mutationFn: () => approvalApi.recallReport(detail.salesId),
    onSuccess: (r) => {
      setPdfMsg({ kind: 'ok', text: `승인을 회수했습니다 — 상태: ${r.status}` });
      afterApprovalChange();
    },
    onError: (e: Error) => setPdfMsg({ kind: 'err', text: e.message }),
  });
  const deleteMut = useMutation({
    mutationFn: () => approvalApi.deleteReportApproval(detail.salesId),
    onSuccess: () => {
      // 결재함 목록·사이드바 배지·영업건 첨부 모두 갱신 후 모달 닫기
      syncAllData(qc);
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      onClose();
    },
    onError: (e: Error) => setPdfMsg({ kind: 'err', text: e.message }),
  });

  /**
   * ESC (v2.231.0 · 사용자 결정 H-4) — 판보를 **쓸 수 있는 사람에게는 입력 창**이라 ESC 로 안 닫힌다.
   *   전에는 ESC 한 번에 적던 「7. 비고」가 확인 없이 사라졌다(점검 실측). 보기만 하는 사람에게는 닫힌다.
   *   위에 자식 팝업(기타비용·매입연결·매입상세·묶기)이 떠 있으면 그 창이 ESC 를 받는다(lib/dialog-stack.ts).
   */
  useDialogLayer(true, canSave ? null : () => {
    if (!managingMisc && !creatingPurchase && peekPurchaseId === null && !pickingMembers) onClose();
  });
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // 묶음 멤버 상세 — 영업상세와 같은 캐시 키 공유 (['salesDetail', id])
  const memberQueries = useQueries({
    queries: memberIds.map((id) => ({
      queryKey: ['salesDetail', id],
      queryFn: () => salesApi.getSalesDetail(id),
      staleTime: 15_000,
    })),
  });
  const memberDetails = memberQueries
    .map((q) => q.data)
    .filter((d): d is SalesDetail => !!d);
  const membersLoading = memberQueries.some((q) => q.isLoading);
  const membersError = memberQueries.some((q) => q.isError);
  const isBundle = memberIds.length > 0;
  const allDetails = [detail, ...memberDetails];

  // 서버 묶음 구성과 재동기화 — 사용자가 이 창에서 묶음을 만지기 전(dirty 아님)까지만.
  //   (승인자가 열어둔 사이 작성자가 재저장했거나, detail 이 refetch 된 경우 낡은 구성 방지.
  //    편집 중(dirty)에는 사용자의 미저장 선택을 덮지 않는다 — 저장 시 서버 응답으로 동기화.)
  const [membersDirty, setMembersDirty] = useState(false);
  const serverMemberKey = (detail.reportMemberIds ?? []).join(',');
  useEffect(() => {
    if (!membersDirty) setMemberIds(detail.reportMemberIds ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverMemberKey, membersDirty]);

  // 손익 — 묶음이면 주건+멤버 전체 합산 (묶음 아니면 기존과 동일)
  const purchaseCost = allDetails.reduce(
    (s, d) => s + d.purchases.reduce((x, p) => x + (p.amount ?? 0), 0),
    0,
  );
  const miscCost = allDetails.reduce(
    (s, d) => s + d.miscCosts.reduce((x, m) => x + m.amount, 0),
    0,
  );
  const totalCost = allDetails.reduce(
    (s, d) =>
      s +
      (d.profit?.totalCost ??
        d.purchases.reduce((x, p) => x + (p.amount ?? 0), 0) +
          d.miscCosts.reduce((x, m) => x + m.amount, 0)),
    0,
  );
  const totalRevenue = allDetails.reduce((s, d) => s + d.proposalPrice, 0);
  const profit = totalRevenue - totalCost;
  const profitRate = totalRevenue > 0 ? (profit / totalRevenue) * 100 : null;

  const issuedAt = new Date();
  const issuedAtStr = `${issuedAt.getFullYear()}-${String(issuedAt.getMonth() + 1).padStart(2, '0')}-${String(
    issuedAt.getDate(),
  ).padStart(2, '0')}`;

  async function handleSave() {
    setPdfMsg(null);
    if (approval?.status === '담당승인' || approval?.status === '부서장승인') {
      const ok = window.confirm(
        `현재 [${approval.status}] 상태입니다.\n저장하면 결재가 처음(담당 검토)부터 다시 시작됩니다.\n계속할까요?`,
      );
      if (!ok) return;
    }
    setPdfBusy(true);
    try {
      const r = await salesApi.saveReportPdf(detail.salesId, reportNote, memberIds);
      const bundleNote = (r.memberSalesIds?.length ?? 0) > 0 ? ` · 묶음 ${r.memberSalesIds!.length + 1}건` : '';
      setPdfMsg({ kind: 'ok', text: `판보 저장 완료 — ${r.filename}${bundleNote} (결재함 [${r.status ?? '검토대기'}] 등재)` });
      if (r.memberSalesIds) setMemberIds(r.memberSalesIds);
      setMembersDirty(false); // 서버와 동기화됨 — 이후 detail 재조회 값으로 다시 따라감
      // 영업건 상세 모달이 다시 열릴 때 첨부가 보이도록 invalidate
      syncAllData(qc);
      afterApprovalChange();
    } catch (e) {
      setPdfMsg({ kind: 'err', text: (e as Error).message });
    } finally {
      setPdfBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center overflow-y-auto py-10"
    >
      <div
        className="bg-white rounded shadow-xl w-[1200px] max-w-[95vw] max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 (인쇄 시 숨김) */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50 sticky top-0 z-10 print:hidden">
          <div className="flex items-center gap-2">
            <h2 className="ui-modal-title">판매보고서</h2>
            {approval?.status && (
              <span
                className={`text-xs px-2 py-0.5 rounded border font-semibold ${APPROVAL_BADGE[approval.status] ?? ''}`}
              >
                {approval.status}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* 결재 액션 (부서장/대표이사/관리자) */}
            {approval?.canApprove && (
              <button
                type="button"
                onClick={() => {
                  const next = NEXT_STATUS[approval.status ?? ''] ?? '승인';
                  if (!window.confirm(`이 판매보고서를 승인합니다 → [${next}]\n계속할까요?`)) return;
                  approveMut.mutate();
                }}
                disabled={approveMut.isPending}
                className="btn btn-success-solid"
              >
                {approveMut.isPending ? '승인 중…' : '✅ 승인'}
              </button>
            )}
            {approval?.canReject && (
              <button
                type="button"
                onClick={() => {
                  const reason = window.prompt('반려 사유를 입력하세요 (작성자에게 표시됩니다)', '');
                  if (reason === null) return;
                  rejectMut.mutate(reason.trim());
                }}
                disabled={rejectMut.isPending}
                className="btn btn-danger"
              >
                {rejectMut.isPending ? '반려 중…' : '⛔ 반려'}
              </button>
            )}
            {approval?.canRecall && (
              <button
                type="button"
                onClick={() => {
                  if (!window.confirm('마지막 승인을 회수(한 단계 되돌림)합니다.\n계속할까요?')) return;
                  recallMut.mutate();
                }}
                disabled={recallMut.isPending}
                className="btn btn-secondary"
                title="부서장승인 → 검토대기 / 최종승인 → 부서장승인 으로 되돌림"
              >
                {recallMut.isPending ? '회수 중…' : '↩ 승인 회수'}
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => {
                  if (
                    !window.confirm(
                      '이 판매보고서(검토대기)를 삭제합니다.\n' +
                        '· 내 결재함에서 제거되고, 첨부된 판보 PDF도 함께 삭제됩니다.\n' +
                        '· 영업건(매출)과 비고 내용은 그대로 유지되며, 다시 [판보 저장] 할 수 있습니다.\n\n' +
                        '계속할까요?',
                    )
                  )
                    return;
                  deleteMut.mutate();
                }}
                disabled={deleteMut.isPending}
                className="btn btn-danger"
                title="검토대기(최초 작성) 상태의 판매보고서를 삭제 — 결재함·PDF 첨부 제거 (영업건은 유지)"
              >
                {deleteMut.isPending ? '삭제 중…' : '🗑 판보 삭제'}
              </button>
            )}
            {canSave && (
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={pdfBusy || membersLoading || membersError}
                className="btn btn-primary"
                title={
                  membersError
                    ? '묶인 영업건을 불러오지 못해 저장할 수 없습니다 — 새로고침 후 다시 시도하세요'
                    : membersLoading
                    ? '묶인 영업건 불러오는 중…'
                    : '판보 PDF 를 본 영업건에 첨부 저장 + 결재함 [검토대기] 등재'
                }
              >
                {pdfBusy ? '저장 중…' : '💾 판보 저장'}
              </button>
            )}
            {canSaveReport && !canSave && approval?.status && !finalApproved && (
              <span
                className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-500"
                title="이 판매보고서는 최초 작성자만 수정할 수 있습니다 (승인자는 [반려]로 처리)"
              >
                작성자 전용 저장
              </span>
            )}
            <button
              type="button"
              onClick={async () => {
                setPdfMsg(null);
                setPdfBusy(true);
                try {
                  const blob = await salesApi.downloadReportPdf(detail.salesId, reportNote, memberIds);
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `판매보고서_${detail.salesId}${memberIds.length > 0 ? `외${memberIds.length}건` : ''}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch (e) {
                  setPdfMsg({ kind: 'err', text: (e as Error).message });
                } finally {
                  setPdfBusy(false);
                }
              }}
              disabled={pdfBusy}
              className="btn btn-danger-solid"
              title="판보 PDF 만 다운로드 (저장 안 함)"
            >
              📄 PDF 다운로드
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="닫기"
              className="ui-close"
            >
              ×
            </button>
          </div>
        </div>
        {pdfMsg && (
          <div
            className={`mx-6 mt-3 text-sm rounded px-3 py-2 print:hidden ${
              pdfMsg.kind === 'ok'
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                : 'bg-rose-50 border border-rose-200 text-rose-700'
            }`}
          >
            {pdfMsg.text}
          </div>
        )}
        {membersError && (
          <div className="mx-6 mt-3 bg-amber-50 border border-amber-300 text-amber-800 text-sm rounded px-3 py-2 print:hidden">
            ⚠ 묶인 영업건 일부를 불러오지 못했습니다 — 아래 합산(매출·원가·손익)이 부정확할 수 있습니다.
            새로고침 후 다시 확인하세요.
          </div>
        )}
        {approval?.status === '반려' && (
          <div className="mx-6 mt-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded px-3 py-2 print:hidden">
            ⛔ 반려됨{approval.rejectedAt ? ` (${fmtShortDate(approval.rejectedAt)})` : ''}
            {approval.rejectReason ? ` — 사유: ${approval.rejectReason}` : ''}
            <span className="block text-xs mt-0.5 text-rose-500">
              내용 수정 후 [판보 저장] 하면 다시 [검토대기] 로 올라갑니다.
            </span>
          </div>
        )}

        {/* 보고서 본문 */}
        <div className="p-8 space-y-5 print:p-12">
          {/* 🔎 승인 검토 도우미 (화면 전용 · PDF 제외) — 승인자가 손익·근거문서·결재이력을 한눈에 */}
          {!isViewer && (
            <ReviewHelperPanel
              detail={detail}
              approval={approval}
              atts={atts}
              isBundle={isBundle}
              noPurchase={allDetails.every((d) => d.purchases.length === 0)}
              totals={{ revenue: totalRevenue, totalCost, purchaseCost, miscCost, profit, profitRate }}
            />
          )}

          {/* 결재판 + 보고서 제목 */}
          <div className="flex items-start justify-between gap-4 border-b-2 border-slate-800 pb-4">
            <div className="flex-1 text-center pt-4">
              <h1 className="text-2xl font-bold tracking-wide">판 매 보 고 서</h1>
              <div className="text-xs text-slate-500 mt-2">
                발행일: {issuedAtStr} &nbsp;·&nbsp; 매출일련번호: {detail.salesId}
                {isBundle ? ` 외 ${memberIds.length}건` : ''}
              </div>
            </div>
            <ApprovalLineBox approval={approval} loading={approvalQuery.isLoading} />
          </div>

          {/* 1. 기본 정보 */}
          <Section title="1. 영업건 기본 정보">
            <ReportTable
              rows={[
                ['매출일련번호', detail.salesId],
                ['사업자', detail.companyId],
                ['영업담당자', detail.empName ?? detail.empId],
                ['진행단계', <span className={stageBadgeClass(detail.status) ?? undefined}>{detail.status}</span>],
                ['최종 갱신일', detail.lastUpdate],
                detail.parentSalesId ? ['분할 원래 건', detail.parentSalesId] : null,
                detail.isTrackRecord ? ['구분', '실적증빙 매출'] : null,
                // 묶음(합본) 판보 — 멤버 관리 (편집 가능할 때) / 목록 표시
                canSave || isBundle
                  ? [
                      '묶인 영업건',
                      <span className="inline-flex items-center flex-wrap gap-1.5">
                        {memberIds.length === 0 && (
                          <span className="text-slate-500 text-xs">없음 — 다른 영업건을 묶어 하나의 판보로 보고할 수 있습니다</span>
                        )}
                        {memberIds.map((id) => (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 text-xs border border-indigo-200 bg-indigo-50 text-indigo-700 rounded px-1.5 py-0.5"
                          >
                            {id}
                            {canSave && (
                              <button
                                type="button"
                                onClick={() => {
                                  setMembersDirty(true);
                                  setMemberIds((prev) => prev.filter((m) => m !== id));
                                }}
                                className="text-indigo-400 hover:text-rose-600 leading-none"
                                title="묶음에서 제외 — [판보 저장] 시 반영"
                              >
                                ×
                              </button>
                            )}
                          </span>
                        ))}
                        {canSave && (
                          <button
                            type="button"
                            onClick={() => setPickingMembers(true)}
                            className="btn btn-outline btn-sm print:hidden"
                            title="같은 담당의 발행 완료 영업건을 이 판보에 묶기 (매출·매입·손익 합산, 결재 1번)"
                          >
                            🔗 영업건 묶기
                          </button>
                        )}
                        {membersLoading && <span className="text-xs text-slate-500"><LoadingLines /></span>}
                      </span>,
                    ]
                  : null,
              ]}
            />
          </Section>

          {/* 2. 거래처 정보 */}
          <Section title="2. 거래처 정보">
            <ReportTable
              rows={[
                ['계산서 발행업체', detail.invoiceCompany ?? '-'],
                ['제안업체', detail.proposalCompany ?? '-'],
                ['엔드유저', detail.endUser ?? '-'],
                ['거래처 담당자', detail.contactPerson ?? '-'],
              ]}
            />
          </Section>

          {/* 3. 프로젝트 정보 — 프로젝트명 옆 분납서, 제안가 옆 견적서·발주서 아이콘 (화면 전용) */}
          <Section title="3. 프로젝트 정보">
            <ReportTable
              rows={[
                [
                  '프로젝트명',
                  <span className="inline-flex items-center flex-wrap gap-y-1">
                    <span>{detail.projectName ?? '-'}</span>
                    {!isViewer && (
                      <DocIcon
                        salesId={detail.salesId}
                        atts={bunapAtts}
                        kind="분할납품요구서"
                        emoji="📄"
                        label="분납서"
                        colorClass="border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                      />
                    )}
                  </span>,
                ],
                ['제안품목 (요약)', detail.itemSummary ?? '-'],
                [
                  '제안가 (VAT별도)',
                  <span className="inline-flex items-center flex-wrap gap-y-1">
                    <span>
                      {`${fmtKrw(isBundle ? totalRevenue : detail.proposalPrice)} 원`}
                      {isBundle && (
                        <span className="text-xs text-slate-500 ml-1.5">
                          (묶음 {memberIds.length + 1}건 합계 — 주건 {fmtKrw(detail.proposalPrice)}원)
                        </span>
                      )}
                    </span>
                    {!isViewer && <QuoteIcons quotes={detail.quotes} />}
                    {!isViewer && (
                      <DocIcon
                        salesId={detail.salesId}
                        atts={poAtts}
                        kind="발주서"
                        emoji="📑"
                        label="발주서"
                        colorClass="border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                      />
                    )}
                  </span>,
                ],
                ['납품완료일', detail.deliveryDate ?? '-'],
                ['계산서 발행일', detail.invoiceDate ?? '-'],
                ['비용참고', detail.reportStatus ?? '-'],
              ]}
            />
            {isBundle && (
              <div className="mt-3">
                <div className="text-xs font-semibold text-indigo-700 mb-1">
                  매출 구성 — 묶음 {memberIds.length + 1}건 (주건 {detail.salesId})
                </div>
                <table className="data-table w-full text-[13px]">
                  <thead>
                    <tr>
                      <th className="text-left w-32">매출일련번호</th>
                      <th className="text-left">프로젝트명</th>
                      <th className="text-left w-36">계산서 발행업체</th>
                      <th className="text-left w-24">발행일</th>
                      <th className="text-right w-32">제안가</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allDetails.map((d) => (
                      <tr key={d.salesId}>
                        <td className="font-mono text-xs">
                          {d.salesId}
                          {d.salesId === detail.salesId && (
                            <span className="text-slate-400 ml-1">(주건)</span>
                          )}
                        </td>
                        <td>{d.projectName ?? '-'}</td>
                        <td className="text-xs">{d.invoiceCompany ?? '-'}</td>
                        <td className="text-xs">{d.invoiceDate ?? '-'}</td>
                        <td className="text-right num font-semibold">{fmtKrw(d.proposalPrice)}</td>
                      </tr>
                    ))}
                    <tr className="font-bold bg-indigo-50">
                      <td colSpan={4} className="text-right">매출 합계</td>
                      <td className="text-right num">{fmtKrw(totalRevenue)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* 4. 영업 품목 — 묶음이면 멤버 품목 포함 (매출번호 컬럼 추가) */}
          {allDetails.some((d) => d.items.length > 0) && (
            <Section title="4. 영업 품목">
              <table className="data-table w-full text-[13px]">
                <thead>
                  <tr>
                    {isBundle && <th className="text-left w-32">매출번호</th>}
                    <th className="text-left">품목명</th>
                    <th className="text-right">수량</th>
                  </tr>
                </thead>
                <tbody>
                  {allDetails.flatMap((d) =>
                    d.items.map((it) => (
                      <tr key={`${d.salesId}-${it.salesItemId}`}>
                        {isBundle && <td className="font-mono text-xs">{d.salesId}</td>}
                        <td>
                          {/* 품명 우선, 없을 때만 코드. P/N 은 주 표시로 쓰지 않음 */}
                          <div>{it.itemName ?? it.itemCode}</div>
                          {it.spec && (
                            <div className="text-xs text-slate-500">{it.spec}</div>
                          )}
                        </td>
                        <td className="text-right num">{it.quantity}</td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            </Section>
          )}

          {/* 5. 매입 내역 — sales+admin 은 [+ 매입 연결] / [+ 기타비용] 가능 (인쇄 시 숨김) */}
          <Section
            title="5. 매입 내역"
            action={
              canAddPurchase && !finalApproved && (
                <div className="flex items-center gap-1.5 print:hidden">
                  <button
                    type="button"
                    onClick={() => setCreatingPurchase(true)}
                    className="btn btn-outline btn-sm"
                    title="매입관리의 기존 재고보유 매입을 검색해서 본 영업건에 연결 — 연결 후 영업진행/매입관리/재고관리 등 모든 화면에 자동 반영"
                  >
                    + 매입 연결
                  </button>
                  <button
                    type="button"
                    onClick={() => setManagingMisc(true)}
                    className="btn btn-warning btn-sm"
                    title="숙박비·운송비 등 매입 외 직접경비 — 매입목록에는 없고 이 영업건 원가에만 합산"
                  >
                    + 기타비용
                  </button>
                </div>
              )
            }
          >
            {allDetails.every((d) => d.purchases.length === 0) ? (
              <div className="text-sm text-slate-400 text-center py-3">
                연결된 매입이 없습니다.
              </div>
            ) : (
              <>
              {!isViewer && (
                <div className="text-xs text-slate-500 mb-1 print:hidden">
                  💡 각 매입 행을 클릭하면 구입 시기·매입처·출처·변경이력을 볼 수 있습니다.
                </div>
              )}
              <table className="data-table w-full text-[13px]">
                <thead>
                  <tr>
                    {isBundle && <th className="text-left w-32">매출번호</th>}
                    <th className="text-left">품목</th>
                    <th className="text-left">매입처</th>
                    <th className="text-right">수량</th>
                    <th className="text-right">단가</th>
                    <th className="text-right">금액</th>
                    <th className="text-left">출고일</th>
                  </tr>
                </thead>
                <tbody>
                  {allDetails.flatMap((d) =>
                    d.purchases.flatMap((p) => [
                      <tr
                        key={p.purchaseId}
                        onClick={() => !isViewer && setPeekPurchaseId(p.purchaseId)}
                        className={!isViewer ? 'cursor-pointer hover:bg-slate-50' : undefined}
                        title={!isViewer ? '클릭하면 이 매입의 상세(구입 시기·출처·변경이력)를 봅니다' : undefined}
                      >
                        {isBundle && <td className="font-mono text-xs">{d.salesId}</td>}
                        <td>
                          {(() => {
                            // 품명 = 품목마스터 품명(우선) → 없으면 자유입력 설명. 파트번호는 표시하지 않음 — 영업진행 매입연결과 동일 규칙.
                            const name = p.itemName || p.description || '-';
                            const specText = (p.itemName ? (p.description || p.spec) : p.spec) || '';
                            return (
                              <>
                                <div className="font-medium">{name}</div>
                                {specText && (
                                  <div className="text-xs text-slate-500 truncate max-w-xs" title={specText}>
                                    {specText}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </td>
                        <td>{p.supplier ?? '-'}</td>
                        <td className="text-right num">{p.orderQty}</td>
                        <td className="text-right num">{p.unitPrice === null ? '-' : fmtKrw(p.unitPrice)}</td>
                        <td className="text-right num font-semibold">
                          {p.amount === null ? '-' : fmtKrw(p.amount)}
                        </td>
                        <td className="text-xs">{p.actualOutDate ?? '-'}</td>
                      </tr>,
                      /* 제조품이면 '매입 당시 원재료'를 그 아래 펼친다 (2026-07-27).
                         위 금액은 이 원재료들의 합계(롤업)라, 아래 줄은 내역 설명이지 별도 비용이 아니다.
                         → 소계에 다시 더하지 않는다. */
                      ...(p.manufacture
                        ? [
                            <tr
                              key={`${p.purchaseId}-mfg-head`}
                              className="bg-indigo-50/40 text-xs text-indigo-800"
                            >
                              {isBundle && <td />}
                              <td colSpan={5} className="py-1">
                                🔧 <strong>{p.manufacture.manufactureNo}</strong> 로 제조 (
                                {p.manufacture.manufactureDate}) — 아래는 이 제품에 들어간
                                <strong> 매입 당시 원재료</strong>이며, 위 금액이 그 합계입니다.
                              </td>
                              <td />
                            </tr>,
                            ...p.manufacture.inputs.map((mi, mIdx) => (
                              <tr
                                key={`${p.purchaseId}-mfg-${mIdx}`}
                                className="bg-indigo-50/20 text-slate-600"
                              >
                                {isBundle && <td />}
                                <td className="pl-6 text-xs">
                                  <span className="text-slate-400">↳ </span>
                                  {mi.itemName ?? '-'}
                                  {mi.spec && (
                                    <span className="text-slate-400"> · {mi.spec}</span>
                                  )}
                                </td>
                                <td className="text-xs">
                                  {mi.supplier ?? '-'}
                                  {mi.actualInDate && (
                                    <span className="text-slate-400"> ({mi.actualInDate} 입고)</span>
                                  )}
                                </td>
                                <td className="text-right num text-xs">{mi.qty}</td>
                                <td className="text-right num text-xs">
                                  {mi.unitPrice === null ? '-' : fmtKrw(mi.unitPrice)}
                                </td>
                                <td className="text-right num text-xs">
                                  {mi.amount === null ? '-' : fmtKrw(mi.amount)}
                                </td>
                                <td />
                              </tr>
                            )),
                          ]
                        : []),
                    ]),
                  )}
                  <tr className="font-bold bg-slate-50">
                    <td colSpan={isBundle ? 5 : 4} className="text-right">매입 소계</td>
                    <td className="text-right num">{fmtKrw(purchaseCost)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
              </>
            )}
            {allDetails.some((d) => d.miscCosts.length > 0) && (
              <div className="mt-3">
                <div className="text-xs font-semibold text-amber-700 mb-1">
                  기타비용 (숙박비·운송비 등 — 매입 외 직접경비)
                </div>
                <table className="data-table w-full text-[13px]">
                  <thead>
                    <tr>
                      {isBundle && <th className="text-left w-32">매출번호</th>}
                      <th className="text-left w-28">지출일</th>
                      <th className="text-left">항목</th>
                      <th className="text-left">비고</th>
                      <th className="text-right w-32">금액</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allDetails.flatMap((d) =>
                      d.miscCosts.map((m) => (
                        <tr key={m.id}>
                          {isBundle && <td className="font-mono text-xs">{d.salesId}</td>}
                          <td className="text-xs">{m.costDate ?? '-'}</td>
                          <td>{m.label}</td>
                          <td className="text-xs text-slate-500">{m.note ?? ''}</td>
                          <td className="text-right num font-semibold">{fmtKrw(m.amount)}</td>
                        </tr>
                      )),
                    )}
                    <tr className="font-bold bg-amber-50">
                      <td colSpan={isBundle ? 4 : 3} className="text-right">기타비용 소계</td>
                      <td className="text-right num">{fmtKrw(miscCost)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* 6. 손익 요약 */}
          <Section title="6. 손익 요약">
            <table className="data-table w-full text-[13px]">
              <thead>
                <tr>
                  <th className="text-right">매출</th>
                  <th className="text-right" title="연결 매입 + 기타비용">원가 (매입+기타)</th>
                  <th className="text-right">예상 이익</th>
                  <th className="text-right">이익률</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-right num">
                    {fmtKrw(totalRevenue)}
                    {isBundle && (
                      <div className="text-[11px] text-slate-500 font-normal">
                        묶음 {memberIds.length + 1}건 합계
                      </div>
                    )}
                  </td>
                  <td className="text-right num">
                    {fmtKrw(totalCost)}
                    {miscCost > 0 && (
                      <div className="text-[11px] text-slate-500 font-normal">
                        매입 {fmtKrw(purchaseCost)} + 기타 {fmtKrw(miscCost)}
                      </div>
                    )}
                  </td>
                  <td className={`text-right num font-bold ${profit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {fmtKrw(profit)}
                  </td>
                  <td className={`text-right num font-bold ${(profitRate ?? 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {fmtPct(profitRate)}
                  </td>
                </tr>
              </tbody>
            </table>
          </Section>

          {/* 7. 비고 — 사용자 입력 (PDF 에도 포함). 최초 작성자만 편집 */}
          <Section title="7. 비고">
            {canSave ? (
              <textarea
                value={reportNote}
                onChange={(e) => setReportNote(e.target.value)}
                placeholder="판매보고서 비고 (선택) — [판보 저장] 시 함께 저장됩니다"
                rows={4}
                className="ui-input ui-textarea w-full resize-y"
              />
            ) : (
              <div className="px-3 py-2 text-sm border border-slate-200 rounded bg-slate-50 whitespace-pre-wrap min-h-[3rem]">
                {reportNote || <span className="text-slate-400">(비고 없음)</span>}
              </div>
            )}
          </Section>

          <div className="text-xs text-slate-500 pt-4 border-t border-slate-200">
            ※ 메모는 내부 협업용으로 본 보고서에서 제외됩니다.<br />
            ※ [💾 판보 저장] 클릭 시 본 보고서가 PDF 로 영업건에 첨부되고, 결재판 작성자 칸에 저장한 사람의 서명·작성일이 들어갑니다.<br />
            ※ 결재 순서: 작성자 작성 → <strong>담당 → 부서장 → 대표이사</strong> 승인. 최종승인 후에는 수정이 잠깁니다.
          </div>

          {/* 매입 상세 팝업 (읽기 전용) — 판보 카드 내부에 렌더해 배경 클릭이 판보 모달로 전파되지 않게 함 */}
          {peekPurchaseId !== null && (
            <PurchasePeekModal purchaseId={peekPurchaseId} onClose={() => setPeekPurchaseId(null)} />
          )}

          {/* 묶음(합본) — 영업건 묶기 팝업. 카드 내부 렌더 (배경 클릭 전파 방지 — PurchasePeek 과 동일).
              선택은 화면 상태에만 반영, [판보 저장] 시 확정 */}
          {canSave && pickingMembers && (
            <BundlePickerModal
              salesId={detail.salesId}
              existing={memberIds}
              onAdd={(ids) => {
                setMembersDirty(true);
                setMemberIds((prev) => [...prev, ...ids.filter((i) => !prev.includes(i))]);
                setPickingMembers(false);
              }}
              onClose={() => setPickingMembers(false)}
            />
          )}
        </div>
      </div>

      {/* 매입 연결 — lazy. 기존 재고보유 매입을 검색해서 본 영업에 연결.
          연결 완료 시 syncAllData 가 sales detail / 매입 / 재고 / 대시보드 등 모든 쿼리 invalidate.
          SalesDetailModal 의 detailQuery 가 refetch 되면서 detail props 가 새로워져 본 모달도 갱신됨. */}
      {canAddPurchase && creatingPurchase && (
        <Suspense fallback={null}>
          <LinkPurchaseModal
            salesId={detail.salesId}
            defaultQuery={detail.endUser}
            onClose={() => setCreatingPurchase(false)}
            onLinked={() => {
              setCreatingPurchase(false);
              syncAllData(qc);
            }}
          />
        </Suspense>
      )}

      {/* 기타비용 — 저장 시 syncAllData 로 detail 재조회 → 본 모달 표에 즉시 반영 */}
      {canAddPurchase && managingMisc && (
        <MiscCostModal
          salesId={detail.salesId}
          miscCosts={detail.miscCosts}
          onClose={() => setManagingMisc(false)}
        />
      )}

    </div>
  );
}

/** 묶음(합본) 판보 — 영업건 묶기 팝업 (v1.6.0).
 *  후보 = 같은 담당 · 계산서 발행 후(발행일 있음) · 매입 전량 출고 · 판보/묶음 미소속.
 *  같은 엔드유저 건이 위로 정렬됨 (분납·동일 프로젝트일 가능성). */
function BundlePickerModal({
  salesId,
  existing,
  onAdd,
  onClose,
}: {
  salesId: string;
  existing: string[];
  onAdd: (ids: string[]) => void;
  onClose: () => void;
}) {
  // 백엔드 zod 상한(memberSalesIds max 30)과 동일 — 초과 선택 시 저장이 원시 검증 에러로
  // 실패하지 않도록 팝업 단계에서 차단
  const MAX_MEMBERS = 30;
  const maxAdd = MAX_MEMBERS - existing.length;
  // 고른 것이 있는 채로 ESC·[취소] 를 누르면 묻는다 (v2.163.0).
  //   markDirty 는 체크 목록에만 달았다 — 검색어만 적은 채 닫는 것까지 물으면 잔소리가 된다.
  const { markDirty, requestClose } = useDirtyClose(onClose);
  const [q, setQ] = useState('');
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const candQuery = useQuery({
    queryKey: ['reportBundleCandidates', salesId],
    queryFn: () => salesApi.listReportBundleCandidates(salesId),
    staleTime: 10_000,
  });
  const list = (candQuery.data ?? []).filter((c) => !existing.includes(c.salesId));
  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? list.filter(
        (c) =>
          c.salesId.toLowerCase().includes(needle) ||
          (c.projectName ?? '').toLowerCase().includes(needle) ||
          (c.invoiceCompany ?? '').toLowerCase().includes(needle) ||
          (c.endUser ?? '').toLowerCase().includes(needle),
      )
    : list;

  function toggle(id: string) {
    // ⭐줄 전체를 눌러도 고를 수 있어서(tr onClick) 체크박스의 change 가 안 날 때가 있다.
    //   「골랐나」는 여기 한 곳에서만 표시한다.
    markDirty();
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <DraggableModal
      onClose={requestClose}
      zIndex={72}
      initialWidth={720}
      initialHeight={560}
      closeOnEsc={false}
      bodyClassName="flex-1 min-h-0 overflow-hidden flex flex-col"
    >
      <div className="dm-drag-handle flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t cursor-move shrink-0">
        <div>
          <h3 className="ui-subsection-title">🔗 영업건 묶기 — 묶음(합본) 판매보고서</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            선택한 영업건이 이 판보에 합산되고, 최종승인 시 함께 &lsquo;판보&rsquo; 단계로 전환됩니다.
            (같은 담당 · 계산서 발행 완료 건만 표시)
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="ui-close"
        >
          ×
        </button>
      </div>
      <div className="px-5 py-2 border-b border-slate-100 shrink-0">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="매출일련번호 · 프로젝트명 · 발행처 · 엔드유저 검색"
          className="ui-input w-full"
        />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-2">
        {candQuery.isLoading && (
          <div className="text-sm text-slate-400 text-center py-8"><LoadingLines /></div>
        )}
        {candQuery.isError && (
          <div className="text-sm text-rose-600 text-center py-8">
            오류: {(candQuery.error as Error).message}
          </div>
        )}
        {!candQuery.isLoading && filtered.length === 0 && (
          <div className="text-sm text-slate-400 text-center py-8">
            묶을 수 있는 영업건이 없습니다.
            <div className="text-xs mt-1">
              (조건: 같은 담당자 · 계산서 발행 후 · 매입 전량 출고 · 판매보고서 미작성)
            </div>
          </div>
        )}
        {filtered.length > 0 && (
          <table className="w-full text-[13px]">
            <thead className="sticky top-0 bg-white">
              <tr className="text-xs text-slate-500 border-b border-slate-200">
                <th className="w-8"></th>
                <th className="text-left py-1.5">매출일련번호</th>
                <th className="text-left">프로젝트명</th>
                <th className="text-left">발행처</th>
                <th className="text-left w-24">발행일</th>
                <th className="text-right w-28">제안가</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.salesId}
                  onClick={() => toggle(c.salesId)}
                  className="border-b border-slate-100 cursor-pointer hover:bg-indigo-50/50"
                >
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={checked.has(c.salesId)}
                      onChange={() => toggle(c.salesId)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td className="font-mono text-xs py-1.5">{c.salesId}</td>
                  <td className="text-xs">{c.projectName ?? '-'}</td>
                  <td className="text-xs">{c.invoiceCompany ?? c.endUser ?? '-'}</td>
                  <td className="text-xs">{c.invoiceDate ?? '-'}</td>
                  <td className="text-right num text-xs font-semibold">{fmtKrw(c.proposalPrice)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b shrink-0">
        <span className="text-xs text-slate-500">
          {checked.size > maxAdd ? (
            <span className="text-rose-600 font-semibold">
              한 판보에는 최대 {MAX_MEMBERS}건까지 묶을 수 있습니다 (현재 {existing.length + checked.size + 1}건)
            </span>
          ) : (
            <>
              {checked.size > 0 ? `${checked.size}건 선택됨` : '묶을 영업건을 선택하세요'}
              <span className="ml-2 text-slate-400">· 추가 후 [💾 판보 저장] 해야 확정됩니다</span>
            </>
          )}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={requestClose}
            className="btn btn-secondary"
          >
            취소
          </button>
          <button
            type="button"
            disabled={checked.size === 0 || checked.size > maxAdd}
            onClick={() => onAdd([...checked])}
            className="btn btn-primary"
          >
            🔗 {checked.size > 0 ? `${checked.size}건 ` : ''}묶기
          </button>
        </div>
      </div>
    </DraggableModal>
  );
}

/** 결재판 4칸 — 영업지원(고정) / 담당 / 부서장 / 대표이사. 서명 이미지는 마이페이지 등록분. */
function ApprovalLineBox({
  approval,
  loading,
}: {
  approval: ReportApprovalInfo | null;
  loading: boolean;
}) {
  if (loading) {
    return <div className="text-xs text-slate-500 pt-4 shrink-0">결재판 불러오는 중…</div>;
  }
  if (!approval) return null;
  return (
    <table className="border-collapse shrink-0 text-center">
      <tbody>
        <tr>
          <th
            rowSpan={2}
            className="border border-slate-400 bg-slate-100 text-[11px] font-semibold px-1 w-5"
            style={{ writingMode: 'vertical-rl' }}
          >
            결재
          </th>
          {approval.cells.map((c) => (
            <th
              key={c.role}
              className="border border-slate-400 bg-slate-100 text-[11px] font-semibold px-1 py-0.5 w-16"
            >
              {c.role === '담당' ? '담 당' : c.role}
            </th>
          ))}
        </tr>
        <tr>
          {approval.cells.map((c) => (
            <td key={c.role} className="border border-slate-400 h-12 w-16 align-middle">
              {c.stamped ? (
                <div className="flex flex-col items-center justify-center h-full">
                  {c.hasSignature && c.userId ? (
                    <img
                      src={approvalApi.signatureImageUrl(c.userId)}
                      alt={c.name}
                      className="max-w-[56px] max-h-[32px] object-contain"
                      onError={(e) => {
                        // 서명 미등록/삭제 → 이름 텍스트로 대체
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <span className="text-xs font-semibold text-slate-700">{c.name}</span>
                  )}
                  {c.stampedAt && (
                    <span className="text-[8px] text-slate-400 leading-none mt-0.5">
                      {fmtShortDate(c.stampedAt)}
                    </span>
                  )}
                </div>
              ) : null}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

function Section({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-slate-700 border-l-4 border-slate-700 pl-2">
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function ReportTable({
  rows,
}: {
  rows: Array<[string, ReactNode] | null>;
}) {
  return (
    <table className="w-full text-[13px] border border-slate-200">
      <tbody>
        {rows
          .filter((r): r is [string, ReactNode] => r !== null)
          .map(([label, value], i) => (
            <tr key={i} className="border-b border-slate-100 last:border-b-0">
              <th className="bg-slate-50 text-left text-xs font-medium text-slate-600 px-3 py-2 w-40 align-top">
                {label}
              </th>
              <td className="px-3 py-2 text-slate-800">{value}</td>
            </tr>
          ))}
      </tbody>
    </table>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 승인 검토 도우미 (v1.5.2) — 승인자가 판보를 승인하기 전 근거를 바로 확인.
 * 모든 아이콘·패널은 화면 전용(print:hidden) — 서버 생성 판보 PDF 와 무관.
 * ───────────────────────────────────────────────────────────── */

/** 제안가 옆 견적서 아이콘 — detail.quotes(연결 견적서)를 클릭 시 PDF 팝업. */
function QuoteIcons({ quotes }: { quotes: LinkedQuote[] }) {
  if (!quotes || quotes.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-1 ml-2 print:hidden align-middle">
      {quotes.map((q) => (
        <button
          key={q.id}
          type="button"
          onClick={() =>
            openServerPdfPreview({
              pdfUrl: quotePdfDownloadUrl(q.id),
              filename: `견적서_${q.quoteNo}.pdf`,
            })
          }
          title={`견적서 ${q.quoteNo} · ${fmtKrw(q.totalWithVat)}원(VAT포함) — 클릭하여 보기`}
          className="btn btn-outline btn-sm"
        >
          📋 견적서
        </button>
      ))}
    </span>
  );
}

/** 첨부 문서(발주서·분납서) 아이콘 — 0건이면 표시 안 함, 1건 즉시 팝업, 2건+ 선택 모달. */
function DocIcon({
  salesId,
  atts,
  kind,
  emoji,
  label,
  colorClass,
}: {
  salesId: string;
  atts: AttachmentMeta[];
  kind: string;
  emoji: string;
  label: string;
  colorClass: string;
}) {
  const [showPicker, setShowPicker] = useState(false);
  if (atts.length === 0) return null;
  return (
    <span className="inline-flex items-center ml-2 print:hidden align-middle">
      <button
        type="button"
        onClick={() => {
          if (atts.length === 1) previewAttachment(atts[0]!);
          else setShowPicker(true);
        }}
        title={`${label} ${atts.length}건 — 클릭하여 보기`}
        className={`btn btn-secondary btn-sm ${colorClass}`}
      >
        {emoji} {label}
        {atts.length > 1 ? ` ${atts.length}` : ''}
      </button>
      {showPicker && (
        <Suspense fallback={null}>
          <AttachmentDownloadModal
            parentKind="sales"
            parentId={salesId}
            kind={kind}
            title={`${emoji} ${label} 선택`}
            zIndex={78}
            onClose={() => setShowPicker(false)}
          />
        </Suspense>
      )}
    </span>
  );
}

/** 승인 검토 도우미 패널 — 손익·저마진 경고·견적 대조·근거문서·결재이력 요약. */
function ReviewHelperPanel({
  detail,
  approval,
  atts,
  isBundle,
  noPurchase,
  totals,
}: {
  detail: SalesDetail;
  approval: ReportApprovalInfo | null;
  atts: AttachmentMeta[];
  isBundle: boolean;
  /** 묶음 포함 전체 기준 매입 0건 여부 (주건만 보지 않음) */
  noPurchase: boolean;
  totals: {
    revenue: number;
    totalCost: number;
    purchaseCost: number;
    miscCost: number;
    profit: number;
    profitRate: number | null;
  };
}) {
  const { totalCost, profit, profitRate } = totals;
  // 묶음(합본)이면 매출 = 주건+멤버 합계
  const rev = totals.revenue;

  // 손익 경고 배지
  let profitBadge: { text: string; cls: string };
  if (profit < 0) {
    profitBadge = { text: '⛔ 역마진 주의', cls: 'bg-rose-100 text-rose-700 border-rose-300' };
  } else if (profitRate !== null && profitRate < 10) {
    profitBadge = { text: '⚠ 저마진', cls: 'bg-amber-100 text-amber-800 border-amber-300' };
  } else {
    profitBadge = { text: '✅ 정상', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' };
  }

  // 견적 대조 (최신 견적 기준) — 견적 총액(VAT포함)을 별도로 환산해 제안가와 비교.
  //   묶음이어도 견적서는 주건에 연결된 문서이므로 '주건 제안가' 와 비교 (합계 아님).
  const headRev = detail.proposalPrice;
  const quotes = detail.quotes ?? [];
  const latestQuote =
    quotes.length > 0
      ? [...quotes].sort(
          (a, b) => (b.quoteDate ?? '').localeCompare(a.quoteDate ?? '') || b.id - a.id,
        )[0]!
      : null;
  const quoteVatExcl = latestQuote ? Math.round(latestQuote.totalWithVat / 1.1) : null;
  const quoteDiff = quoteVatExcl !== null ? headRev - quoteVatExcl : null;
  const quoteMatch = quoteDiff !== null && Math.abs(quoteDiff) <= 100;

  // 근거 문서 체크리스트
  const has = (k: string) => atts.some((a) => a.kind === k);
  const docs = [
    { label: '견적서', ok: detail.hasQuote },
    { label: '발주서', ok: has('발주서') },
    { label: '분납서', ok: has('분할납품요구서') },
    { label: '납품확인증', ok: has('납품확인증') },
  ];

  return (
    <div className="mb-5 rounded border border-slate-200 bg-slate-50 p-3 text-sm print:hidden">
      <div className="text-xs font-bold text-slate-500 mb-2">
        🔎 승인 검토 도우미{' '}
        <span className="font-normal">(승인 전 확인용 · 보고서 PDF 에는 포함되지 않습니다)</span>
      </div>
      <div className="space-y-2">
        {/* 손익 */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="text-slate-500 w-16 shrink-0 text-xs">손익</span>
          <span>
            매출 <b>{fmtKrw(rev)}</b>
            {isBundle && <span className="text-xs text-slate-500 ml-1">(묶음 합계)</span>}
          </span>
          <span>
            원가 <b>{fmtKrw(totalCost)}</b>
          </span>
          <span>
            이익{' '}
            <b className={profit >= 0 ? 'text-emerald-700' : 'text-rose-700'}>{fmtKrw(profit)}</b>
          </span>
          <span>
            이익률{' '}
            <b className={(profitRate ?? 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}>
              {fmtPct(profitRate)}
            </b>
          </span>
          <span className={`text-xs px-2 py-0.5 rounded border font-semibold ${profitBadge.cls}`}>
            {profitBadge.text}
          </span>
        </div>
        {noPurchase && (
          <div className="flex items-center gap-2">
            <span className="text-slate-500 w-16 shrink-0 text-xs">매입</span>
            <span className="text-xs px-2 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-300">
              ℹ 연결된 매입 없음 — 기술지원·서비스 등 매입 없는 건이면 정상
            </span>
          </div>
        )}
        {/* 견적 대조 */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-slate-500 w-16 shrink-0 text-xs">견적 대조</span>
          {latestQuote ? (
            <>
              <span>
                견적서 <b>{latestQuote.quoteNo}</b> · {fmtKrw(latestQuote.totalWithVat)}(VAT포함) →
                별도 <b>{fmtKrw(quoteVatExcl!)}</b>
              </span>
              <span>
                제안가{isBundle ? '(주건)' : ''} <b>{fmtKrw(headRev)}</b>
              </span>
              {quoteMatch ? (
                <span className="text-xs px-2 py-0.5 rounded border font-semibold bg-emerald-100 text-emerald-700 border-emerald-300">
                  ✅ 일치
                </span>
              ) : (
                <span className="text-xs px-2 py-0.5 rounded border font-semibold bg-amber-100 text-amber-800 border-amber-300">
                  ⚠ 차이 {fmtKrw(Math.abs(quoteDiff!))}
                </span>
              )}
              {quotes.length > 1 && (
                <span className="text-xs text-slate-500">견적 {quotes.length}건 중 최신 기준</span>
              )}
            </>
          ) : (
            <span className="text-xs text-slate-500">연결된 견적서 없음</span>
          )}
        </div>
        {/* 근거 문서 */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-slate-500 w-16 shrink-0 text-xs">근거 문서</span>
          {docs.map((d) => (
            <span key={d.label} className={d.ok ? 'text-emerald-700' : 'text-slate-400'}>
              {d.ok ? '✅' : '⚪'} {d.label}
            </span>
          ))}
        </div>
        {/* 결재 이력 */}
        <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
          <span className="text-slate-500 w-16 shrink-0 text-xs mt-0.5">결재 이력</span>
          {approval && approval.status ? (
            <ApprovalTimeline approval={approval} />
          ) : (
            <span className="text-xs text-slate-500">아직 판보 저장(결재 상신) 전입니다.</span>
          )}
        </div>
      </div>
    </div>
  );
}

/** 결재 이력 타임라인 — 결재판 4칸의 승인 시점을 시간순으로. */
function ApprovalTimeline({ approval }: { approval: ReportApprovalInfo }) {
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs">
      {approval.cells.map((c, i) => (
        <span key={c.role} className="flex items-center gap-1">
          {i > 0 && <span className="text-slate-300">→</span>}
          <span
            className={`px-1.5 py-0.5 rounded border ${
              c.stamped
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-white border-slate-200 text-slate-400'
            }`}
          >
            <b>{c.role}</b> {c.name}
            {c.stamped && c.stampedAt ? ` ${fmtShortDate(c.stampedAt)}` : ' · 대기'}
          </span>
        </span>
      ))}
      {approval.status === '반려' && (
        <span className="px-1.5 py-0.5 rounded border bg-rose-50 border-rose-200 text-rose-700">
          ⛔ 반려{approval.rejectedAt ? ` ${fmtShortDate(approval.rejectedAt)}` : ''}
          {approval.rejectReason ? ` — ${approval.rejectReason}` : ''}
        </span>
      )}
    </div>
  );
}

/** 매입 상세 팝업 (읽기 전용) — 매입 행 클릭 시 구입 시기·출처·변경이력 표시. */
function PurchasePeekModal({ purchaseId, onClose }: { purchaseId: number; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['purchaseDetail', purchaseId],
    queryFn: () => purchaseApi.getPurchaseDetail(purchaseId),
  });
  const d = q.data;

  const rows: Array<[string, ReactNode] | null> = d
    ? [
        ['품목', d.itemName ?? d.itemCode ?? '-'],
        d.spec ? ['규격', d.spec] : null,
        ['매입처', d.supplier ?? '-'],
        ['구입 사업자', d.purchaseCompanyId ?? '-'],
        ['매입 유형', d.type ?? '-'],
        ['발주일', d.orderDate ?? '-'],
        ['입고일', d.actualInDate ?? '-'],
        ['매입계산서 발행일', d.purchaseInvoiceDate ?? '-'],
        ['출고일', d.actualOutDate ?? '-'],
        ['수량', String(d.orderQty)],
        ['단가', d.unitPrice === null ? '-' : `${fmtKrw(d.unitPrice)} 원`],
        ['금액', d.amount === null ? '-' : `${fmtKrw(d.amount)} 원`],
        d.ntsApprovalNo ? ['국세청 승인번호', d.ntsApprovalNo] : null,
        d.reviewStatus ? ['확인 상태', d.reviewStatus] : null,
        d.note ? ['비고', d.note] : null,
      ]
    : [];

  return (
    <DraggableModal
      onClose={onClose}
      zIndex={72}
      initialWidth={560}
      initialHeight={620}
      closeOnEsc /* 매입 엿보기 — 보기 창 (v2.231.0 · H-4) */
      bodyClassName="flex-1 min-h-0 overflow-hidden flex flex-col"
    >
      <div className="dm-drag-handle flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t cursor-move shrink-0">
        <div>
          <h3 className="ui-subsection-title">📦 매입 상세 (읽기 전용)</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            이 매입을 언제·어디서 구입했는지 · 수정은 매입관리에서
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="ui-close"
        >
          ×
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {q.isLoading && (
          <div className="text-sm text-slate-400 text-center py-8"><LoadingLines /></div>
        )}
        {q.isError && (
          <div className="text-sm text-rose-600 text-center py-8">
            오류: {(q.error as Error).message}
          </div>
        )}
        {d && (
          <>
            <table className="w-full text-[13px]">
              <tbody>
                {rows
                  .filter((r): r is [string, ReactNode] => r !== null)
                  .map(([label, value], i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <th className="bg-slate-50 text-left text-xs font-medium text-slate-600 px-4 py-2 w-36 align-top">
                        {label}
                      </th>
                      <td className="px-4 py-2 text-slate-800">{value}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <PurchaseAuditSection purchaseId={purchaseId} />
          </>
        )}
      </div>
    </DraggableModal>
  );
}
